// Deals: a company holds any number, at most one open per pipeline. The lead
// card's id is its company id, and a deal migrated from crm_leads reuses it.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './core'
import { afterDealWrite, dealsKey } from './dealCache'
import type { PersonRef, StageCategory } from './processConfig'

export type CustomValue = string | number | boolean | string[]
export type CustomValues = Record<string, CustomValue>

export interface Deal {
  id: string
  companyId: string
  pipeline: { id: string; name: string; isDefault: boolean }
  stage: { id: string; label: string; category: StageCategory; color: string | null }
  /** 'open' until closed; afterwards the category of the stage it closed in. */
  outcome: StageCategory
  owner: PersonRef | null
  closer: PersonRef | null
  value: number | null
  currency: string | null
  nextStepAt: string | null
  nextStepNote: string | null
  lostReason: { id: string; value: string; label: string } | null
  openedAt: string
  closedAt: string | null
  stageChangedAt: string | null
  custom: CustomValues
}

export interface DealPatch {
  value?: number | null
  currency?: string | null
  stageId?: string
  nextStepAt?: string | null
  nextStepNote?: string | null
  /** Keys to set. null clears a key; keys not named are kept. */
  custom?: Record<string, CustomValue | null>
}

export interface DealOpen {
  pipelineId?: string
  stageId?: string
  custom?: CustomValues
}

export interface DealClose {
  outcome: 'won' | 'lost'
  stageId?: string
  lostReasonId?: string
  note?: string
}

export function useCompanyDeals(companyId: string | null, enabled = true) {
  return useQuery({
    queryKey: dealsKey(companyId ?? ''),
    queryFn: () => apiFetch<{ deals: Deal[] }>(`/api/companies/${companyId}/deals`),
    enabled: enabled && !!companyId,
    staleTime: 30_000,
  })
}

export function useOpenDeal(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: DealOpen) =>
      apiFetch<Deal>(`/api/companies/${companyId}/deals`, { method: 'POST', body }),
    onSuccess: deal => afterDealWrite(qc, deal),
  })
}

export function useUpdateDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: DealPatch }) =>
      apiFetch<Deal>(`/api/deals/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: deal => afterDealWrite(qc, deal),
  })
}

export function useCloseDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DealClose }) =>
      apiFetch<Deal>(`/api/deals/${id}/close`, { method: 'POST', body }),
    onSuccess: deal => afterDealWrite(qc, deal),
  })
}
